import * as XLSX from "xlsx";

// ── Regexes ─────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
// Looser pattern for extracting an email embedded inside other text
const EMAIL_EMBEDDED_RE = /[\w.+\-']+@[\w\-]+\.[\w.]{2,}/i;

// ── Header synonym sets ──────────────────────────────────────────────────────
// normalizeKey collapses whitespace, underscores, and hyphens so both
// "E-Mail Address" and "emailaddress" hit the same bucket.

const EMAIL_EXACT = new Set([
  "email", "emailaddress", "email address",
  "emai", "e-mail", "e-mail address", "emailaddr",
  "primaryemail", "primary email", "primary email address", "primary_email",
  "workemail", "work email", "work email address",
  "businessemail", "business email",
  "contactemail", "contact email",
  "recipientemail", "recipient email",
  "clientemail", "client email",
  "customeremail", "customer email",
  "mail", "mailaddress", "mail address",
  "corpemail", "corp email", "officeemail", "office email",
]);

const NAME_EXACT = new Set([
  "name", "fullname", "full name", "displayname", "display name",
  "contactname", "contact name", "recipientname", "recipient name",
  "clientname", "client name", "customername", "customer name",
  "company", "companyname", "company name", "organisation", "organization",
  "person", "personname", "person name",
]);

const FIRST_EXACT = new Set([
  "first", "firstname", "first name", "givenname", "given name", "forename",
]);

const LAST_EXACT = new Set([
  "last", "lastname", "last name", "surname", "familyname", "family name",
]);

// ── Normalisation helpers ────────────────────────────────────────────────────

// Loose: collapse all non-alpha chars → "worke mail address" → "workemailaddress"
const stripAll  = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
// Readable: collapse runs of whitespace/separators to a single space
const readable  = (s) => String(s || "").toLowerCase().trim().replace(/[\s_\-]+/g, " ");

function classifyHeader(raw) {
  const a = stripAll(raw);
  const r = readable(raw);

  if (EMAIL_EXACT.has(a) || EMAIL_EXACT.has(r)) return "email";
  // Fuzzy: any header that contains "email" or "e-mail" probably holds email addresses
  if (a.includes("email") || r.includes("e-mail") || r.includes("e mail")) return "email_fuzzy";
  if (NAME_EXACT.has(a)  || NAME_EXACT.has(r))  return "name";
  if (FIRST_EXACT.has(a) || FIRST_EXACT.has(r)) return "first";
  if (LAST_EXACT.has(a)  || LAST_EXACT.has(r))  return "last";
  return null;
}

// ── Email extraction ─────────────────────────────────────────────────────────

function extractEmail(raw) {
  if (!raw) return "";
  const s = String(raw).trim();

  // "Display Name <email@domain.com>"
  const angle = s.match(/<([^>]+)>/);
  if (angle) {
    const c = angle[1].trim();
    if (EMAIL_RE.test(c)) return c.toLowerCase();
  }

  // Multiple addresses separated by comma / semicolon / whitespace
  if (/[,;]/.test(s)) {
    for (const part of s.split(/[,;\s]+/)) {
      if (EMAIL_RE.test(part.trim())) return part.trim().toLowerCase();
    }
  }

  // Plain address
  if (EMAIL_RE.test(s)) return s.toLowerCase();

  // Embedded in arbitrary text: "Email: john@example.com" or similar
  const inline = s.match(EMAIL_EMBEDDED_RE);
  if (inline && EMAIL_RE.test(inline[0])) return inline[0].toLowerCase();

  return "";
}

// ── Name helpers ─────────────────────────────────────────────────────────────

function nameFromEmail(email) {
  const local = email.split("@")[0];
  return local
    .replace(/[._+\-]+/g, " ")
    .replace(/\d+$/, "")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ── Header-row detection ─────────────────────────────────────────────────────

function pickHeaderRow(rows2d) {
  for (let r = 0; r < Math.min(rows2d.length, 30); r++) {
    const row = rows2d[r] || [];
    let emailIdx = -1, fuzzyIdx = -1, nameIdx = -1, firstIdx = -1, lastIdx = -1;

    row.forEach((cell, idx) => {
      const role = classifyHeader(cell);
      if (role === "email"       && emailIdx === -1) emailIdx = idx;
      if (role === "email_fuzzy" && fuzzyIdx  === -1) fuzzyIdx  = idx;
      if (role === "name"        && nameIdx   === -1) nameIdx   = idx;
      if (role === "first"       && firstIdx  === -1) firstIdx  = idx;
      if (role === "last"        && lastIdx   === -1) lastIdx   = idx;
    });

    // Prefer exact match; fall back to fuzzy
    const resolvedEmail = emailIdx !== -1 ? emailIdx : fuzzyIdx;
    if (resolvedEmail !== -1) {
      return { headerRow: r, emailIdx: resolvedEmail, nameIdx, firstIdx, lastIdx };
    }
  }
  return null;
}

// ── Fallback: scan every column for the most email-like data ─────────────────

function scanForEmailColumn(rows2d) {
  if (rows2d.length < 1) return null;
  const colCount = Math.max(...rows2d.map((r) => (r || []).length));
  let bestCol = -1, bestCount = 0;

  for (let c = 0; c < colCount; c++) {
    let count = 0;
    for (const row of rows2d) {
      if (extractEmail((row || [])[c])) count++;
    }
    if (count > bestCount) { bestCount = count; bestCol = c; }
  }

  if (bestCol === -1 || bestCount < 1) return null;
  // No header row — start parsing from row 0
  return { headerRow: -1, emailIdx: bestCol, nameIdx: -1, firstIdx: -1, lastIdx: -1 };
}

// ── Sheet parser ─────────────────────────────────────────────────────────────

function parseSheet(ws) {
  const rows2d = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: "",
    raw: false,
    blankrows: false,
  });
  if (!rows2d?.length) return [];

  const headerInfo = pickHeaderRow(rows2d) ?? scanForEmailColumn(rows2d);
  if (!headerInfo) return [];

  const startRow = headerInfo.headerRow + 1;
  const contacts = [];

  for (let r = startRow; r < rows2d.length; r++) {
    const row = rows2d[r] || [];

    // Skip obviously empty rows
    if (row.every((c) => !String(c).trim())) continue;

    const email = extractEmail(row[headerInfo.emailIdx]);
    if (!email) continue;

    let name = "";
    if (headerInfo.nameIdx !== -1) {
      name = String(row[headerInfo.nameIdx] || "").trim();
    } else {
      const first = headerInfo.firstIdx !== -1 ? String(row[headerInfo.firstIdx] || "").trim() : "";
      const last  = headerInfo.lastIdx  !== -1 ? String(row[headerInfo.lastIdx]  || "").trim() : "";
      name = (first || last) ? `${first} ${last}`.trim() : "";
    }

    contacts.push({
      id: `imp-${r}-${email}`,
      name: name || nameFromEmail(email),
      email,
      _kind: "raw_email",
    });
  }

  return contacts;
}

// ── De-duplication ───────────────────────────────────────────────────────────

function uniqByEmail(arr) {
  const seen = new Set();
  const out = [];
  for (const c of arr) {
    if (!c.email || seen.has(c.email)) continue;
    seen.add(c.email);
    out.push(c);
  }
  return out;
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function parseExcelOrCsv(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(new Uint8Array(buf), { type: "array" });

  // Try every sheet; keep the one that produces the most contacts
  let best = [];
  for (const sheetName of wb.SheetNames) {
    const contacts = parseSheet(wb.Sheets[sheetName]);
    if (contacts.length > best.length) best = contacts;
  }

  return uniqByEmail(best);
}
